import { z } from 'zod';
import { FEATURE_LOCALES } from '../features/catalogue.js';
import { idSchema, nonNegativeQuantitySchema, positiveQuantitySchema } from './common.js';
export const shippingDocumentTypeSchema = z.enum(['QUOTE', 'ORDER', 'DELIVERY_NOTE', 'INVOICE']);
export const packagingLabelsSchema = z.record(z.enum(FEATURE_LOCALES), z.string().trim().min(1).max(120));
export const packagingTypeWriteSchema = z
    .object({
    code: z
        .string()
        .trim()
        .regex(/^[A-Z][A-Z0-9_]{0,29}$/u),
    translations: packagingLabelsSchema.refine((labels) => FEATURE_LOCALES.every((locale) => labels[locale]), 'shipping.checkFields'),
    sortOrder: z.number().int().min(0).max(10000).default(0),
    isActive: z.boolean().default(true),
})
    .strict();
export const packagingTypeSchema = packagingTypeWriteSchema.extend({
    id: idSchema,
    translations: packagingLabelsSchema,
});
export const packagingTypesSchema = z.object({ data: z.array(packagingTypeSchema) });
export const shippingSettingsSchema = z
    .object({
    packagingEnabled: z.boolean().default(false),
    weightEnabled: z.boolean().default(false),
    packagingTypeIds: z
        .array(idSchema)
        .max(50)
        .refine((ids) => new Set(ids).size === ids.length)
        .default([]),
    weightMode: z.enum(['NET', 'GROSS', 'BOTH']).default('NET'),
    documentTypes: z
        .array(shippingDocumentTypeSchema)
        .default(['QUOTE', 'ORDER', 'DELIVERY_NOTE', 'INVOICE']),
})
    .strict();
export const logisticsBasisSchema = z.object({
    productType: z.string().nullable(),
    baseUnit: z.string(),
    massKgPerBase: z.string().nullable(),
    weightNetKg: z.string().nullable(),
    weightGrossKg: z.string().nullable(),
    packagings: z.array(z.object({
        id: idSchema,
        packagingTypeId: idSchema.nullable(),
        factor: positiveQuantitySchema,
        weightGrossKg: z.string().nullable(),
        isSummaryDefault: z.boolean(),
        isWeightDefault: z.boolean(),
    })),
});
export const shippingIssueSchema = z.object({
    position: z.number().int(),
    productId: idSchema.nullable(),
    name: z.string(),
    field: z.string(),
    reason: z.enum([
        'missingBasis',
        'missingFactor',
        'ambiguousVariant',
        'missingNet',
        'missingGross',
        'negativeQuantity',
    ]),
});
const metricSchema = z.object({ value: z.string().nullable(), complete: z.boolean() });
export const shippingCalculationSchema = z.object({
    packages: z.array(metricSchema.extend({ typeId: idSchema })),
    pieceCount: metricSchema.optional(),
    netKg: metricSchema,
    grossKg: metricSchema,
    issues: z.array(shippingIssueSchema),
    relevantCount: z.number().int(),
});
export const shippingActualValuesSchema = z
    .object({
    packages: z
        .array(z.object({ typeId: idSchema, quantity: nonNegativeQuantitySchema }))
        .max(50)
        .refine((rows) => new Set(rows.map((row) => row.typeId)).size === rows.length),
    netKg: nonNegativeQuantitySchema.nullable(),
    grossKg: nonNegativeQuantitySchema.nullable(),
    reason: z.string().trim().max(1000).nullable(),
})
    .strict();
export const shippingActualSchema = shippingActualValuesSchema.extend({
    confirmedById: idSchema,
    confirmedAt: z.string().datetime(),
    basisFingerprint: z.string(),
    needsReview: z.boolean(),
});
export const shippingSummarySchema = z.object({
    version: z.literal(1),
    capturedAt: z.string().datetime(),
    settings: shippingSettingsSchema,
    types: z.array(packagingTypeSchema),
    calculation: shippingCalculationSchema,
    basisFingerprint: z.string(),
    actual: shippingActualSchema.nullable(),
});
export const shippingConfirmationSchema = z
    .object({ version: z.number().int().positive(), actual: shippingActualValuesSchema.nullable() })
    .strict();
//# sourceMappingURL=shipping-summary.js.map